/**
 * "Request mapping" (AIC-987): the one action on the empty states that say the caller's account isn't mapped to an analyst
 * (My profile, Today, Assignments, Review's two lists). It asks an admin to map the account: the server raises one open request per
 * account (POST /me/mapping-request), which Admin → People shows beside the person. Asked once, it says so and is not offered
 * again until an admin has answered. Nothing is emailed.
 *
 * Not shown in analyst preview (nothing is changed there), to an admin (who maps accounts in Admin → People themselves), nor when
 * the server says this account may not ask (its sign-in address is another account's: an admin has that already).
 * A page with two such empty states (Review) shares one `useMappingRequest`, so both say "Mapping requested" once asked.
 */
import { useState } from "react";
import type { PortalApi } from "../api/client.ts";
import { useLoad, usePortal } from "../api/hooks.ts";
import { isAdmin } from "../roles.ts";
import { busyProps, useSave } from "../screens/adminParts.tsx";
import { shortDate } from "../screens/peopleModel.ts";

export interface MappingRequestState {
  /** Whether the button belongs on the page at all. */
  readonly show: boolean;
  /** The open request (read, or just made); null when there is none. */
  readonly open: { readonly at: string } | null;
  readonly busy: boolean;
  readonly ask: () => void;
}

/** The caller's mapping request, read only while `active` (an unmapped empty state is on the page). */
export function useMappingRequest(active: boolean): MappingRequestState {
  const { preview, roles } = usePortal();
  const on = active && !preview && !isAdmin(roles);
  const load = useLoad((api) => (on ? api.mappingRequest() : Promise.resolve(null)), [on]);
  const [asked, setAsked] = useState<{ at: string } | null>(null);
  const save = useSave();
  const data = load.state === "ready" ? load.data : null;
  // Mapped after all (an admin got there first, or the page is stale), or not this account's to ask: nothing to offer.
  const show = on && !(data && (data.mapped || !data.can_request));
  const ask = async () => {
    if (save.busy) return;
    const out = await save.run({
      busy: "Asking an admin…", err: "The request wasn't sent",
      ok: (r) => (r.existing
        ? { title: `You already asked (${shortDate(r.request.at)}).`, desc: "Your request is still open; an admin maps your account in People." }
        : { title: "Mapping requested.", desc: "An admin maps your account in People. Your work shows here once they have." }),
    }, (api: PortalApi) => api.requestMapping());
    if (out) setAsked(out.request);
  };
  return { show, open: asked ?? data?.request ?? null, busy: save.busy, ask: () => void ask() };
}

/** The button; with `state`, a page's shared request, else its own. */
export function RequestMapping({ state }: { readonly state?: MappingRequestState }) {
  const own = useMappingRequest(state === undefined);
  const s = state ?? own;
  if (!s.show) return null;
  if (s.open) return <button type="button" className="ipd-btn" disabled>Mapping requested ({shortDate(s.open.at)})</button>;
  return <button type="button" className="ipd-btn" {...busyProps(s.busy)} onClick={s.ask}>Request mapping</button>;
}
